import React from "react";
import { Check } from "lucide-react";
import Header from "../components/site/Header";
import Seo from "../components/site/Seo";
import Footer from "../components/site/Footer";
import CTAButton from "../components/site/CTAButton";
import Photo from "../components/site/Photo";
import ShootPlanner from "../components/site/ShootPlanner";
import {
  MelbourneSection, FAQSection, FinalCTA, Breadcrumb, SisterServices,
} from "../components/site/ServiceSections";

export default function MenuShootCostPlanner() {
  return (
    <div className="min-h-screen bg-ink text-bone">
      <Seo />
      <Header />
      <Hero />
      <Reasoning />
      <ShootPlanner />
      <Trust />
      <BelowToolContext />
      <MelbourneSection
        serviceLabel="Menu Shoot Cost"
        eyebrow="Coverage · All Melbourne, no travel fees"
        headline="A transparent menu-shoot cost"
        italic="for every Melbourne suburb."
        intro="The estimator above is the same one we use internally. Final quotes are confirmed by phone — usually inside five minutes — and they don't move from what the planner shows you."
      />
      <SisterServices keys={["menu", "food", "ubereats", "cafe"]} />
      <FAQSection
        eyebrow="Common questions · Menu shoot cost Melbourne"
        headline="Pricing, scope,"
        italic="and what's actually included."
        intro="If your question isn't here, call directly — most quotes confirmed in under five minutes."
        faqs={FAQS}
      />
      <FinalCTA
        eyebrow="Final step · Hand-confirmed quotes"
        headline="Ready to lock"
        italic="your shoot day?"
        intro="Quick form below or call directly. The number from the planner above is the number we invoice — no surprises."
        backdropSlug="menu-malai-tikka"
      />
      <Footer />
    </div>
  );
}

const Hero = () => (
  <section className="relative min-h-[80svh] overflow-hidden bg-ink">
    <div className="absolute inset-0">
      <Photo slug="hero-spread-01" alt="Menu shoot cost planner Melbourne" priority ratio="auto" className="!relative h-full w-full" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/45 to-ink" />
    <div className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/30 to-transparent" />

    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 min-h-[80svh] flex flex-col pt-24 sm:pt-28 lg:pt-32 pb-10 lg:pb-12">
      <Breadcrumb current="Menu Shoot Cost Planner" />

      <div className="flex-1 flex flex-col justify-center max-w-5xl">
        <p className="text-xs sm:text-sm uppercase tracking-wider2 text-ember mb-5 sm:mb-7 font-bold">
          Interactive · Real costing in under 60 seconds
        </p>
        <h1 className="font-display tracking-editorial leading-[0.92]" style={{ fontSize: "clamp(2.5rem, 7vw, 6rem)" }}>
          Cost your menu shoot
          <br />
          before <span className="italic font-light text-ember">you call.</span>
        </h1>
        <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg md:text-xl leading-relaxed text-bone/85">
          Use the planner below to scope dishes, drinks, desserts, venue and
          reels. The number it returns is the number we&apos;ll quote — confirmed
          by phone within one business day, no fine print.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#planner"><CTAButton variant="primary" size="lg">Open The Planner</CTAButton></a>
          <a href="tel:0494756111"><CTAButton variant="ghost" size="lg" arrow={false}>Call 0494 756 111</CTAButton></a>
        </div>
      </div>
    </div>
  </section>
);

const REASONS = [
  { t: "Volume-tier pricing", d: "$30 per image at small menus, $25 mid-range, $10 per image once you hit 100+ images. The planner does the maths for you." },
  { t: "Add-ons are obvious", d: "Drinks, desserts, venue and reels each have a clear line — you can see exactly how each toggle changes the total." },
  { t: "No surprises later", d: "What the planner returns is what the written quote returns. We invoice that number, not more." },
];

const Reasoning = () => (
  <section className="relative bg-ink text-bone py-20 lg:py-28 overflow-hidden">
    <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
    <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-16 mb-12 items-end">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs uppercase tracking-wider2 text-ember mb-5 font-bold">Why we built this</p>
          <h2 className="font-display tracking-editorial leading-[0.95]" style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}>
            Hospitality photography
            <br />
            <span className="italic font-light text-ember">should be costable.</span>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-5 lg:pb-3">
          <p className="text-bone/75 text-base md:text-lg leading-relaxed max-w-md">
            Most photographers quote in conversation. We built a planner so
            you can sanity-check the cost before the call — and lock in a
            written quote on the same day.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-bone/10 border border-bone/10">
        {REASONS.map((r) => (
          <div key={r.t} className="bg-ink p-7 md:p-8">
            <Check size={16} strokeWidth={2} className="text-ember mb-4" />
            <h3 className="font-display text-xl md:text-2xl tracking-editorial leading-tight">{r.t}</h3>
            <p className="mt-4 text-sm md:text-base text-bone/75 leading-relaxed">{r.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Trust = () => (
  <section className="bg-bone text-ink py-20 lg:py-28">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
        {[
          ["No travel fees", "Inside Greater Melbourne"],
          ["5–7 days", "Standard turnaround"],
          ["1 free re-edit", "Per dish, no questions"],
          ["Full licence", "Owned + earned channels"],
        ].map(([n, d]) => (
          <div key={n} className="bg-bone p-7 md:p-8">
            <p className="font-sans font-bold tracking-tight leading-none" style={{ fontSize: "clamp(1.25rem, 2vw, 1.75rem)" }}>{n}</p>
            <p className="mt-3 text-sm text-char-mute leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const BelowToolContext = () => (
  <section className="bg-bone text-ink pb-20 lg:pb-28">
    <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-8 lg:gap-12">
        <div className="col-span-12 lg:col-span-7">
          <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-4">What the number above actually buys you</p>
          <p className="text-base md:text-lg text-char leading-relaxed">
            A typical $850 menu shoot for a 22-dish Vietnamese restaurant
            in Footscray buys a half-day on-site shoot, 22 finished images
            in 3 crops each (1:1 Uber Eats, 4:5 Instagram, 16:9 website),
            colour-graded delivery in 5 working days, one free re-edit per
            dish, and full commercial licence for your owned and earned
            channels. A $1,950 shoot for a 60-dish two-trade venue in the
            CBD buys a full day on-site, 60 hero images plus Uber Eats and
            Google Business assets, 5–8 vertical Reels clips, plus a
            second photographer for venue interior. Everything bigger
            (multi-venue rollouts, franchise campaigns, OOH-grade brand
            campaigns) is quoted bespoke — usually inside 48 hours.
          </p>
        </div>
        <div className="col-span-12 lg:col-span-5">
          <p className="text-xs uppercase tracking-wider2 text-ember font-bold mb-4">Three real Melbourne examples</p>
          <ul className="space-y-4 text-sm md:text-base text-char-mute">
            <li><strong className="text-ink block">Suburb café, Brunswick — $540</strong> 14 dishes, brunch + coffee + venue sweep, half-day morning shoot before doors.</li>
            <li><strong className="text-ink block">Mid restaurant, Carlton — $980</strong> 26 dishes, menu refresh + Uber Eats + Instagram supporting set, half-day shoot.</li>
            <li><strong className="text-ink block">Group venue, Southbank — $1,950</strong> 60 dishes, full library + Reels + Google Business, full-day shoot with second photographer.</li>
          </ul>
        </div>
      </div>
    </div>
  </section>
);

export const FAQS = [
  { q: "Is the planner price accurate for a 22-dish Footscray restaurant or a 60-dish CBD venue?", a: "Yes — accurate within ~5% for both. A 22-dish Vietnamese venue in Footscray with menu + Uber Eats + Instagram typically lands at $850–$920 (no travel fee, half-day shoot). A 60-dish two-trade CBD venue with full menu + Reels + Google Business typically lands at $1,950–$2,100 (full day, second photographer). The written quote we send within 24 hours will match the planner number." },
  { q: "Why does the per-image rate drop from $30 to $10 at 100+ images?", a: "Production overhead per image goes down dramatically as menu size grows. The lighting setup, surface prep and colour grading workflow are amortised across more shots — a 12-image menu carries the same 2-hour set-up time as a 120-image menu, but the per-image cost of that setup drops 10x. We pass the saving on directly rather than keep it as margin." },
  { q: "Can I bolt Reels, Google Business or drone exterior onto the planner output?", a: "Yes — Reels packs from $180 (3 clips) to $620 (15 clips), Google Business asset packs from $240 (cover + 10 dishes + interior/exterior), drone exterior $220 for CASA-approved sub-25kg sorties (relevant for Southbank rooftop venues, Brunswick warehouse conversions, beachside Brighton). Each toggle in the planner has its own line — nothing is hidden in the base price." },
  { q: "Do you ever quote higher than the planner figure?", a: "Only for genuinely bespoke briefs — multi-venue groups (3+ sites), regional travel outside Greater Melbourne (e.g. Bellarine, Yarra Valley, Mornington Peninsula), urgent <24-hour turnarounds, OOH-grade brand campaigns with talent, or unusual access (rooftop, multi-floor, restricted CBD loading docks). We always flag the difference on the call before the written quote." },
  { q: "Is GST included in the planner figure?", a: "No — all numbers in the planner are ex-GST. GST is added on the invoice at the current ATO rate of 10%. So a $980 planner figure invoices at $1,078 inc-GST. We&apos;re GST-registered and you can claim it back as a business expense." },
];
